import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/feature/Toast";
import ToggleSwitch from "@/components/base/ToggleSwitch";
import type { Promotion } from "@/lib/promotions";

interface PromoForm {
  title: string;
  subtitle: string;
  cta_text: string;
  cta_link: string;
  image_url: string;
  active: boolean;
}

const EMPTY_FORM: PromoForm = {
  title: "",
  subtitle: "",
  cta_text: "Ver promoción",
  cta_link: "/#destacados",
  image_url: "",
  active: true,
};

/**
 * Gestión del anuncio promocional a pantalla completa (móvil).
 * Permite editar texto, imagen, enlace y activarlo/desactivarlo.
 */
export default function PromotionsTab() {
  const { showToast } = useToast();
  const [form, setForm] = useState<PromoForm>(EMPTY_FORM);
  const [promoId, setPromoId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    supabase
      .from("promotions")
      .select("*")
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data, error }) => {
        if (!mounted) return;
        if (!error && data) {
          const p = data as Promotion;
          setPromoId(p.id);
          setForm({
            title: p.title ?? "",
            subtitle: p.subtitle ?? "",
            cta_text: p.cta_text ?? "Ver promoción",
            cta_link: p.cta_link ?? "/#destacados",
            image_url: p.image_url ?? "",
            active: p.active ?? true,
          });
        }
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const update = (key: keyof PromoForm, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    if (!form.title.trim()) {
      showToast("El título es obligatorio.", "error");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        subtitle: form.subtitle.trim(),
        cta_text: form.cta_text.trim() || "Ver promoción",
        cta_link: form.cta_link.trim(),
        image_url: form.image_url.trim(),
        active: form.active,
        updated_at: new Date().toISOString(),
      };
      if (promoId) {
        const { error } = await supabase
          .from("promotions")
          .update(payload)
          .eq("id", promoId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("promotions").insert(payload);
        if (error) throw error;
      }
      showToast("Promoción guardada correctamente.", "success");
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "No se pudo guardar la promoción.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="h-72 animate-pulse rounded-lg bg-background-100" />
          <div className="space-y-4">
            <div className="h-10 animate-pulse rounded-md bg-background-100" />
            <div className="h-10 animate-pulse rounded-md bg-background-100" />
            <div className="h-10 animate-pulse rounded-md bg-background-100" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <p className="text-sm text-foreground-600">
        Este anuncio aparece a pantalla completa cuando un usuario entra a la
        app desde su móvil (una vez por sesión). Edita el contenido y actívalo o
        desactívalo cuando quieras.
      </p>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Vista previa móvil */}
        <div className="overflow-hidden rounded-lg border border-background-200/70">
          <div className="relative mx-auto aspect-[9/19] max-w-[240px] overflow-hidden bg-background-100">
            {form.image_url ? (
              <img
                src={form.image_url}
                alt={form.title || "Vista previa de promoción"}
                className="h-full w-full object-cover object-top"
              />
            ) : (
              <span className="flex h-full w-full flex-col items-center justify-center gap-2 text-foreground-400">
                <i className="ri-image-add-line text-3xl"></i>
                <span className="text-xs">Vista previa</span>
              </span>
            )}
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/80"></div>
            <div className="absolute inset-x-0 bottom-0 p-4">
              <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur-sm">
                <i className="ri-sparkling-2-line text-accent-300"></i>
                Oferta exclusiva
              </span>
              <p className="mt-2 font-heading text-lg font-bold leading-tight text-white">
                {form.title || "Título de la promoción"}
              </p>
              {form.subtitle && (
                <p className="mt-1 line-clamp-2 text-[11px] text-white/85">
                  {form.subtitle}
                </p>
              )}
              <div className="mt-3 rounded-full bg-white py-2 text-center text-xs font-semibold text-foreground-950 dark:text-foreground-50">
                {form.cta_text || "Ver promoción"}
              </div>
            </div>
          </div>
        </div>

        {/* Formulario */}
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-700">
              Título
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="Semana de Ofertas"
              className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-700">
              Subtítulo
            </label>
            <input
              type="text"
              value={form.subtitle}
              onChange={(e) => update("subtitle", e.target.value)}
              placeholder="Hasta 40% en electrónica, moda y hogar"
              className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground-700">
                Texto del botón
              </label>
              <input
                type="text"
                value={form.cta_text}
                onChange={(e) => update("cta_text", e.target.value)}
                placeholder="Explorar ofertas"
                className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground-700">
                Enlace del botón
              </label>
              <input
                type="text"
                value={form.cta_link}
                onChange={(e) => update("cta_link", e.target.value)}
                placeholder="/#destacados"
                className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
              />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-700">
              URL de la imagen (fondo)
            </label>
            <input
              type="text"
              value={form.image_url}
              onChange={(e) => update("image_url", e.target.value)}
              placeholder="https://..."
              className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div
            className={`flex w-full items-center justify-between rounded-md border px-4 py-3 text-sm font-medium transition-colors ${
              form.active
                ? "border-secondary-300 bg-secondary-50 text-secondary-800"
                : "border-background-200 bg-background-100 text-foreground-600"
            }`}
          >
            <span className="flex items-center gap-2">
              <i
                className={
                  form.active
                    ? "ri-megaphone-fill text-secondary-600"
                    : "ri-megaphone-line text-foreground-400"
                }
              ></i>
              {form.active ? "Anuncio activo" : "Anuncio desactivado"}
            </span>
            <ToggleSwitch
              checked={form.active}
              onChange={(v) => update("active", v)}
              label="Activar o desactivar el anuncio promocional"
            />
          </div>

          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? (
              <i className="ri-loader-4-line animate-spin"></i>
            ) : (
              <i className="ri-save-line"></i>
            )}
            {saving ? "Guardando..." : "Guardar promoción"}
          </button>
        </div>
      </div>
    </div>
  );
}